import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { dietService } from '../../services/dietService';
import { Price, CreatePriceRequest, UpdatePriceRequest } from '../../types/diet';

interface Props {
  price?: Price | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PriceModal: React.FC<Props> = ({ price, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!price;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreatePriceRequest>({
    defaultValues: { name: price?.name || '', description: price?.description || '', amount: price?.amount || 0, currency: price?.currency || 'USD', type: (price as any)?.type || '', status: price?.status || 'ACTIVE' }
  });

  useEffect(() => { if (price) reset({ name: price.name, description: price.description || '', amount: price.amount, currency: price.currency, type: (price as any).type || '', status: price.status }); }, [price, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreatePriceRequest) => dietService.createPrice(data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create price'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdatePriceRequest) => dietService.updatePrice(price!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to update price'),
  });

  const onSubmit = (data: CreatePriceRequest) => { setError(null); isEditing ? updateMutation.mutate(data) : createMutation.mutate(data); };
  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Price' : 'Create Price'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="form-label">Name *</label>
                    <input {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter price name" />
                    {errors.name && <p className="form-error">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="form-label">Description</label>
                    <textarea {...register('description')} className="form-input" rows={2} placeholder="Optional description" />
                  </div>
                  <div>
                    <label className="form-label">Price Category *</label>
                    <select {...register('type', { required: 'Price Category is required' as any })} className="form-input">
                      <option value="">Select category</option>
                      <option value="Per Day">Per Day</option>
                      <option value="Per Meal">Per Meal</option>
                      <option value="Half Board AM">Half Board AM</option>
                      <option value="Lunch Box">Lunch Box</option>
                      <option value="Half Board PM">Half Board PM</option>
                    </select>
                    {errors as any && (errors as any).type && <p className="form-error">{(errors as any).type.message as any}</p>}
                  </div>
                  <div>
                    <label className="form-label">Amount *</label>
                    <input {...register('amount', { required: 'Amount is required', valueAsNumber: true })} type="number" step="0.01" className="form-input" placeholder="Enter amount" />
                    {errors.amount && <p className="form-error">{String(errors.amount.message || 'Invalid amount')}</p>}
                  </div>
                  <div>
                    <label className="form-label">Currency</label>
                    <input {...register('currency')} type="text" className="form-input" placeholder="Currency code (e.g. USD)" />
                  </div>
                  <div>
                    <label className="form-label">Status</label>
                    <select {...register('status')} className="form-input">
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" loading={isLoading}>{isEditing ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PriceModal;

